import React, { useState, useEffect } from 'react';
import {
  User,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Phone,
  Mail,
  Building2,
  X,
  FileText
} from 'lucide-react';
import { useToast } from '../components/shared/ToastProvider';
import ContactDetailModal from '../components/ContactDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = ['Active', 'Inactive', 'On Hold'];
const INVOICE_FORMAT_OPTIONS = [
  'All placements as one invoice',
  'Each placement in separate invoice',
  'Placement grouped by variable'
];

const ContactModal = ({ isOpen, onClose, onSave, title, formData, setFormData, vendors, vendorIdFromUrl }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl my-8 mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-2xl font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="col-span-2">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter first name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter last name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nickname
              </label>
              <input
                type="text"
                value={formData.nickname}
                onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter nickname"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {STATUS_OPTIONS.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vendor <span className="text-red-500">*</span>
              </label>
              {vendorIdFromUrl ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50">
                  {vendors.find(v => v.id === vendorIdFromUrl)?.company_name || 'Loading...'}
                </div>
              ) : (
                <select
                  value={formData.vendor_id}
                  onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a vendor</option>
                  {vendors.map(vendor => (
                    <option key={vendor.id} value={vendor.id}>{vendor.company_name}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Contact Details */}
            <div className="col-span-2 mt-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.primary_email}
                onChange={(e) => setFormData({...formData, primary_email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="primary@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Secondary Email
              </label>
              <input
                type="email"
                value={formData.secondary_email}
                onChange={(e) => setFormData({...formData, secondary_email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="secondary@email.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+1-555-0123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Owner <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.owner}
                onChange={(e) => setFormData({...formData, owner: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Owner name/email"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Full address"
              />
            </div>

            {/* Invoice Settings */}
            <div className="col-span-2 mt-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Invoice Settings</h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Frequency
              </label>
              <select
                value={formData.invoice_frequency}
                onChange={(e) => setFormData({...formData, invoice_frequency: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select frequency</option>
                <option value="Weekly">Weekly</option>
                <option value="Bi-weekly">Bi-weekly</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Net Terms (days)
              </label>
              <input
                type="number"
                value={formData.net_terms}
                onChange={(e) => setFormData({...formData, net_terms: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="30"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Format <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.invoice_format}
                onChange={(e) => setFormData({...formData, invoice_format: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {INVOICE_FORMAT_OPTIONS.map(format => (
                  <option key={format} value={format}>{format}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {title.includes('Create') ? 'Create Contact' : 'Update Contact'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ContactManagement = () => {
  const { showToast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailContact, setDetailContact] = useState(null);
  
  // Get vendor from URL if filtering by specific vendor
  const urlParams = new URLSearchParams(window.location.search);
  const vendorIdFromUrl = urlParams.get('vendor');
  const isEmbedded = urlParams.get('embedded') === 'true';
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    nickname: '',
    status: 'Active',
    vendor_id: vendorIdFromUrl || '',
    primary_email: '',
    secondary_email: '',
    phone_number: '',
    address: '',
    owner: '',
    invoice_frequency: '',
    invoice_format: 'All placements as one invoice',
    net_terms: ''
  });

  useEffect(() => {
    fetchVendors();
    fetchContacts();
  }, [vendorIdFromUrl]);

  const fetchVendors = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/companies`);
      if (response.ok) {
        const data = await response.json();
        setVendors(data.companies || []);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/contacts`;
      if (vendorIdFromUrl) {
        url += `?vendor_id=${vendorIdFromUrl}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts || []);
      } else {
        showToast('Failed to fetch contacts', 'error');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      showToast('Error loading contacts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = async () => {
    if (!formData.first_name || !formData.last_name || !formData.status || 
        !formData.vendor_id || !formData.primary_email || !formData.phone_number || 
        !formData.owner) {
      showToast('Please fill all mandatory fields', 'warning');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          created_by: 'current_user@juno.com'
        })
      });

      if (response.ok) {
        showToast('Contact created successfully', 'success');
        setShowCreateModal(false);
        resetForm();
        fetchContacts();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to create contact', 'error');
      }
    } catch (error) {
      console.error('Error creating contact:', error);
      showToast('Error creating contact', 'error');
    }
  };

  const handleUpdateContact = async () => {
    if (!selectedContact) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${selectedContact.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showToast('Contact updated successfully', 'success');
        setShowEditModal(false);
        setSelectedContact(null);
        resetForm();
        fetchContacts();
      } else {
        const error = await response.json();
        showToast(error.detail || 'Failed to update contact', 'error');
      }
    } catch (error) {
      console.error('Error updating contact:', error);
      showToast('Error updating contact', 'error');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contactId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showToast('Contact deleted successfully', 'success');
        fetchContacts();
      } else {
        showToast('Failed to delete contact', 'error');
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      showToast('Error deleting contact', 'error');
    }
  };

  const openEditModal = (contact) => {
    setSelectedContact(contact);
    setFormData({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      nickname: contact.nickname || '',
      status: contact.status || 'Active',
      vendor_id: contact.vendor_id || '',
      primary_email: contact.primary_email || '',
      secondary_email: contact.secondary_email || '',
      phone_number: contact.phone_number || '',
      address: contact.address || '',
      owner: contact.owner || '',
      invoice_frequency: contact.invoice_frequency || '',
      invoice_format: contact.invoice_format || 'All placements as one invoice',
      net_terms: contact.net_terms || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      nickname: '',
      status: 'Active',
      vendor_id: '',
      primary_email: '',
      secondary_email: '',
      phone_number: '',
      address: '',
      owner: '',
      invoice_frequency: '',
      invoice_format: 'All placements as one invoice',
      net_terms: ''
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-green-100 text-green-800',
      'Inactive': 'bg-gray-100 text-gray-800',
      'On Hold': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const filteredContacts = contacts.filter(contact => {
    const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) ||
           contact.primary_email?.toLowerCase().includes(search) ||
           contact.vendor_name?.toLowerCase().includes(search);
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contacts</h1>
          <p className="text-gray-600 mt-1">Manage vendor contacts</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          <span>Add Contact</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center justify-end space-x-2">
            <span className="text-sm text-gray-600">
              {filteredContacts.length} contacts
            </span>
          </div>
        </div>
      </div>

      {/* Contacts Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading contacts...</p>
        </div>
      ) : filteredContacts.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
          <p className="text-gray-600">Get started by adding your first contact</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                {!vendorIdFromUrl && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice Settings
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContacts.map((contact) => (
                <tr 
                  key={contact.id} 
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => {
                    setDetailContact(contact);
                    setShowDetailModal(true);
                  }}
                  data-testid={`contact-row-${contact.id}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {contact.first_name} {contact.last_name}
                        </div>
                        {contact.nickname && (
                          <div className="text-sm text-gray-500">"{contact.nickname}"</div>
                        )}
                      </div>
                    </div>
                  </td>
                  {!vendorIdFromUrl && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-900">
                        <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                        {contact.vendor_name || '-'}
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(contact.status)}`}>
                      {contact.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center mb-1">
                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                        {contact.primary_email}
                      </div>
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        {contact.phone_number}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center mb-1">
                        <FileText className="w-4 h-4 mr-2 text-gray-400" />
                        {contact.invoice_format?.substring(0, 20)}...
                      </div>
                      {contact.net_terms && (
                        <div className="text-xs text-gray-500">Net {contact.net_terms} days</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          setDetailContact(contact);
                          setShowDetailModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                        data-testid={`edit-contact-${contact.id}`}
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteContact(contact.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                        data-testid={`delete-contact-${contact.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Contact Modal */}
      <ContactModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        onSave={handleCreateContact}
        title="Create New Contact"
        formData={formData}
        setFormData={setFormData}
        vendors={vendors}
        vendorIdFromUrl={vendorIdFromUrl}
      />

      {/* Edit Contact Modal */}
      <ContactModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedContact(null);
          resetForm();
        }}
        onSave={handleUpdateContact}
        title="Edit Contact"
        formData={formData}
        setFormData={setFormData}
        vendors={vendors}
        vendorIdFromUrl={vendorIdFromUrl}
      />

      {/* Contact Detail Modal */}
      <ContactDetailModal
        contact={detailContact}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setDetailContact(null);
        }}
        onEdit={(contact) => {
          setShowDetailModal(false);
          openEditModal(contact);
        }}
      />
    </div>
  );
};

export default ContactManagement;
